import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { getCurrencySymbol, ICurrencySymbols } from '@brixtol/currency-symbols';
import { useSelector } from 'react-redux';

import { Consolidation } from '@futrli-sdk/consolidations';
import { consolidationByIdSelector } from '@futrli/shared-services';
import { fetchConsolidation } from '@futrli/organisations';
import { FinancialConfig } from '@futrli-sdk/predict';
import { StoreState } from 'packages/skeleton/src/core/redux-utils/types';

import { ConsolidationEntity, useConsolidationEntities } from '../../hooks/useConsolidationEntities';
import { getFYConfigFromDayMonthEnd } from '../../utils/financial-year-end';

interface ConsolidationDataContextProps {
  consolidationId: string;
  children?: ReactNode;
  workspaceId: string;
}

interface ConsolidationDataContextValues {
  consolidationId: string;
  consolidation?: Consolidation;
  currencySymbol: string;
  entities?: ConsolidationEntity[];
  financialConfig?: FinancialConfig;
  workspaceId: string;
}

const context = createContext(({} as unknown) as ConsolidationDataContextValues);

export const ConsolidationDataContext = ({ children, consolidationId, workspaceId }: ConsolidationDataContextProps) => {
  const [consolidation, setConsolidation] = useState<Consolidation | undefined>();
  const consolidationFromStore = useSelector((store: StoreState) => {
    return consolidationByIdSelector(store.normalised, consolidationId);
  });

  const { data: entities } = useConsolidationEntities(consolidation);

  const financialConfig = useMemo(() => {
    if (consolidation) {
      return getFYConfigFromDayMonthEnd(consolidation.financial_year_end_day, consolidation.financial_year_end_month);
    }
  }, [consolidation]);

  useEffect(() => {
    const updateConsolidation = async () => {
      const fetchedConsolidation = await fetchConsolidation(workspaceId, consolidationId);
      setConsolidation(fetchedConsolidation);
    };
    if (consolidationFromStore) {
      setConsolidation(consolidationFromStore);
    } else {
      updateConsolidation();
    }
  }, [consolidationFromStore, workspaceId, consolidationId]);

  const values = {
    consolidationId,
    consolidation,
    currencySymbol: consolidation?.display_currency
      ? getCurrencySymbol(consolidation.display_currency as keyof ICurrencySymbols)
      : '?',
    entities,
    financialConfig,
    workspaceId
  };

  return <context.Provider value={values}>{children}</context.Provider>;
};

export const useConsolidationDataContext = () => useContext(context);
